import { useEffect, useState } from "react";
import { list, remove } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { ResourceForm } from "@/components/ResourcePage";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import EventSelect from "@/components/EventSelect";
import { PAYMENT_STATUSES, INVOICE_STATUSES, fmtIDR, fmtDate, APP_LOGO } from "@/lib/constants";
import { useEventOptions } from "@/hooks/useEventOptions";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, Eye, Printer, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { downloadPdf } from "@/lib/pdf";
import api, { formatApiError } from "@/lib/api";

export default function Invoices() {
  const { can } = useAuth();
  const { events } = useEventOptions();
  const [invoices, setInvoices] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [genEvent, setGenEvent] = useState("");

  const load = () => list("invoices").then(setInvoices).catch(() => setInvoices([]));
  useEffect(() => { load(); }, []);

  const del = async (id) => { await remove("invoices", id); toast.success("Dihapus"); load(); };
  const generate = async (force = false) => {
    if (!genEvent || genEvent === "all") { toast.error("Pilih event dulu"); return; }
    try {
      const inv = await api.post(`/invoices/generate/${genEvent}${force ? "?force=true" : ""}`).then((r) => r.data);
      toast.success(`Invoice ${inv.invoice_number} dibuat`); setGenOpen(false); setGenEvent(""); load();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.warning(e.response.data.detail, { description: "Buat invoice baru untuk event ini?", action: { label: "Tetap Buat", onClick: () => generate(true) }, duration: 9000 });
      } else { toast.error(formatApiError(e.response?.data?.detail)); }
    }
  };

  const fields = [
    { name: "invoice_number", label: "Nomor Invoice", required: true },
    { name: "event_id", label: "Event", type: "select", options: events.map((e) => ({ value: e.id, label: `${e.groom} & ${e.bride}` })) },
    { name: "client_name", label: "Nama Client", required: true, full: true },
    { name: "wedding_date", label: "Tanggal Wedding", type: "date" },
    { name: "venue", label: "Venue" },
    { name: "package", label: "Paket" },
    { name: "description", label: "Deskripsi", type: "textarea", full: true },
    { name: "subtotal", label: "Subtotal", type: "number" },
    { name: "discount", label: "Diskon", type: "number" },
    { name: "grand_total", label: "Grand Total", type: "number" },
    { name: "paid", label: "Sudah Dibayar", type: "number" },
    { name: "remaining", label: "Sisa", type: "number" },
    { name: "due_date", label: "Jatuh Tempo", type: "date" },
    { name: "status", label: "Status", type: "select", options: INVOICE_STATUSES, default: "Draft" },
  ];

  return (
    <div>
      <PageHeader overline="Invoice Generator" title="Invoices" subtitle="Buat invoice profesional dan cetak untuk client.">
        {can(4) && <Button variant="outline" onClick={() => setGenOpen(true)} data-testid="invoices-generate-btn" className="rounded-full"><Sparkles className="w-4 h-4 mr-1" />Generate dari Event</Button>}
        {can(4) && <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="invoices-add-btn" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full"><Plus className="w-4 h-4 mr-1" />Invoice</Button>}
      </PageHeader>

      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-beige/60 hover:bg-beige/60 border-gold/20">
                {["No. Invoice", "Client", "Grand Total", "Sisa", "Status", "Aksi"].map((h) => <TableHead key={h} className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-[#7A7A7A]">Belum ada invoice.</TableCell></TableRow>
              ) : invoices.map((inv) => (
                <TableRow key={inv.id} className="border-gold/10 hover:bg-beige/30" data-testid="invoice-row">
                  <TableCell className="font-mono text-sm text-burgundy font-semibold">{inv.invoice_number}</TableCell>
                  <TableCell className="text-sm font-medium text-[#1A1A1A]">{inv.client_name}</TableCell>
                  <TableCell className="text-sm">{fmtIDR(inv.grand_total)}</TableCell>
                  <TableCell className="text-sm">{fmtIDR(inv.remaining)}</TableCell>
                  <TableCell><StatusBadge status={inv.status} /></TableCell>
                  <TableCell className="whitespace-nowrap">
                    <button onClick={() => setPreview(inv)} className="p-1.5 text-[#7A7A7A] hover:text-burgundy" data-testid="invoice-preview"><Eye className="w-4 h-4" /></button>
                    {can(4) && <>
                      <button onClick={() => { setEditing(inv); setFormOpen(true); }} className="p-1.5 text-[#7A7A7A] hover:text-burgundy" data-testid="invoice-edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(inv.id)} className="p-1.5 text-[#7A7A7A] hover:text-red-600" data-testid="invoice-delete"><Trash2 className="w-4 h-4" /></button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <ResourceForm open={formOpen} onOpenChange={setFormOpen} fields={fields} initial={editing} resource="invoices" onSaved={load} title={editing ? "Edit Invoice" : "Buat Invoice"} />

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-burgundy">Generate Invoice dari Event</DialogTitle></DialogHeader>
          <p className="text-sm text-[#7A7A7A] -mt-2">Invoice akan dibuat otomatis dari data event & pembayaran (nama client, tanggal, paket, total, sudah dibayar, sisa).</p>
          <div className="py-2"><EventSelect value={genEvent} onChange={setGenEvent} includeAll={false} className="w-full bg-white rounded-lg" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>Batal</Button>
            <Button onClick={() => generate()} data-testid="invoice-generate-confirm" className="bg-burgundy hover:bg-burgundy-dark text-white">Generate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          <VisuallyHidden><DialogTitle>Preview Invoice</DialogTitle></VisuallyHidden>
          {preview && <InvoiceDoc inv={preview} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InvoiceDoc({ inv }) {
  return (
    <div>
      <div id="invoice-doc" className="p-8 bg-white">
        <div className="flex items-start justify-between border-b-2 border-burgundy pb-5 mb-6">
          <div className="flex items-center gap-3">
            <img src={APP_LOGO} alt="Primrose Organizer" className="h-14 w-auto object-contain" />
            <div>
              <h2 className="font-heading text-2xl text-burgundy leading-none">PRIMROSE ORGANIZER</h2>
              <p className="text-[11px] tracking-[0.2em] text-gold-dark uppercase">Wedding Organizer With You</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-heading text-3xl text-[#1A1A1A]">INVOICE</p>
            <p className="font-mono text-sm text-[#7A7A7A]">{inv.invoice_number}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6 text-sm">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gold-dark font-semibold mb-1">Ditagihkan Kepada</p>
            <p className="font-semibold text-[#1A1A1A]">{inv.client_name}</p>
            <p className="text-[#7A7A7A]">{inv.venue}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-[#7A7A7A]">Wedding Date: <span className="text-[#1A1A1A]">{fmtDate(inv.wedding_date)}</span></p>
            <p className="text-sm text-[#7A7A7A]">Due Date: <span className="text-[#1A1A1A]">{fmtDate(inv.due_date)}</span></p>
            <div className="mt-1"><StatusBadge status={inv.status} /></div>
          </div>
        </div>

        <table className="w-full text-sm mb-6">
          <thead>
            <tr className="bg-beige text-[#4A4A4A] text-[11px] uppercase tracking-wider">
              <th className="text-left py-2 px-3">Deskripsi</th>
              <th className="text-right py-2 px-3">Jumlah</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gold/20">
              <td className="py-3 px-3">
                <p className="font-semibold text-[#1A1A1A]">{inv.package}</p>
                <p className="text-xs text-[#7A7A7A]">{inv.description}</p>
              </td>
              <td className="py-3 px-3 text-right">{fmtIDR(inv.subtotal)}</td>
            </tr>
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className="w-64 space-y-1.5 text-sm">
            <div className="flex justify-between text-[#7A7A7A]"><span>Subtotal</span><span>{fmtIDR(inv.subtotal)}</span></div>
            <div className="flex justify-between text-[#7A7A7A]"><span>Diskon</span><span>- {fmtIDR(inv.discount)}</span></div>
            <div className="flex justify-between font-semibold text-[#1A1A1A] border-t border-gold/30 pt-1.5"><span>Grand Total</span><span>{fmtIDR(inv.grand_total)}</span></div>
            <div className="flex justify-between text-emerald-600"><span>Dibayar</span><span>{fmtIDR(inv.paid)}</span></div>
            <div className="flex justify-between font-bold text-burgundy text-base border-t-2 border-burgundy pt-1.5"><span>Sisa</span><span>{fmtIDR(inv.remaining)}</span></div>
          </div>
        </div>

        <div className="mt-10 pt-5 border-t border-gold/20 text-center">
          <p className="font-heading text-lg text-burgundy italic">Thank you for trusting Primrose Organizer.</p>
        </div>
      </div>
      <div className="px-8 pb-6 flex justify-end gap-2 print:hidden">
        <Button variant="outline" onClick={() => downloadPdf("invoice-doc", `${inv.invoice_number}.pdf`)} data-testid="invoice-download-pdf" className="rounded-full"><Download className="w-4 h-4 mr-1" />Download PDF</Button>
        <Button onClick={() => window.print()} data-testid="invoice-print" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full"><Printer className="w-4 h-4 mr-1" />Print</Button>
      </div>
    </div>
  );
}
